import { useState } from 'react'

import { posicionPrecisa } from '@/lib/nativo/ubicacion'
import type { MiHerramienta } from '@/lib/supabase/tipos-bd'
import {
  useColaboradoresActivos,
  useOrdenesActivas,
  useSolicitarTraslado,
} from '@/modules/programacion/useHerramientas'

import { useHerramientaEnCola } from './useColaDeAcciones'
import { useCancelarRecogida, usePedirRecogida } from './useRecogidas'
import estilos from './MisHerramientas.module.css'

/**
 * Lo que el técnico lleva encima.
 *
 * ES UNA SECCIÓN DE «MI TRABAJO», NO UNA PANTALLA. Un técnico lleva una o dos
 * herramientas; darle una entrada de menú propia lo obligaría a navegar para
 * ver dos renglones. Su teléfono es una sola pantalla que se recorre hacia
 * abajo, y esto es una parada más en ese recorrido.
 *
 * NO TIENE BOTÓN DE DEVOLVER, y es a propósito. La herramienta vuelve cuando
 * alguien en bodega la tiene en la mano y la recibe. Si el técnico pudiera
 * marcarla como devuelta desde la calle, el inventario diría que la escalera
 * está en bodega y la escalera estaría en su moto.
 *
 * SÍ PUEDE PEDIR UN TRASLADO, y desde 0122 a dos sitios: a otra orden, o a
 * OTRO TÉCNICO. El segundo es el que de verdad pasa —«se la dejo a Fabián, que
 * mañana arranca con ella»— y antes no se podía registrar, así que se hacía
 * igual pero por fuera del sistema. Ninguno se mueve solo: queda pendiente y la
 * herramienta sigue a su nombre hasta que gerencia lo apruebe.
 *
 * Y PUEDE PEDIR QUE LA RECOJAN (0239): la deja en un punto —la obra, o donde
 * esté— y un operario pasa por ella. Sigue a su nombre hasta que ese operario
 * la escanee en bodega. Las tres cosas funcionan sin señal: se guardan en el
 * teléfono y salen solas.
 */

type Props = {
  herramientas: MiHerramienta[]
}

export function MisHerramientas({ herramientas }: Props) {
  const { ordenes } = useOrdenesActivas()
  const { colaboradores } = useColaboradoresActivos()
  const solicitar = useSolicitarTraslado()
  const pedirRecogida = usePedirRecogida()
  const cancelarRecogida = useCancelarRecogida()
  const enCola = useHerramientaEnCola()

  const [trasladando, setTrasladando] = useState<string | null>(null)
  const [aQuien, setAQuien] = useState<'orden' | 'tecnico'>('orden')
  const [destino, setDestino] = useState('')
  const [motivo, setMotivo] = useState('')
  const [aviso, setAviso] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // Cuál está mostrando cómo se devuelve.
  const [devolviendo, setDevolviendo] = useState<string | null>(null)
  // La recogida: cuál, dónde la deja, la nota y, si la pidió «aquí», el GPS.
  const [recogiendo, setRecogiendo] = useState<string | null>(null)
  const [dondeLaDeja, setDondeLaDeja] = useState('')
  const [nota, setNota] = useState('')
  const [aqui, setAqui] = useState<{ latitud: number; longitud: number } | null>(null)
  const [ubicando, setUbicando] = useState(false)

  if (herramientas.length === 0) return null

  // Una semana con una herramienta encima ya no es «la estoy usando».
  const olvidada = herramientas.some((h) => h.dias_afuera >= 7)

  const enColaDe = (clase: string, llave: string | null) =>
    llave ? enCola.find((a) => a.clase === clase && a.ordenTrabajoId === llave) : undefined

  function abrirRecogida(h: MiHerramienta) {
    setRecogiendo(h.prestamo_id)
    setTrasladando(null)
    setDevolviendo(null)
    // Lo normal es dejarla en la obra de su orden: se propone esa dirección.
    setDondeLaDeja(h.direccion_obra ?? '')
    setNota('')
    setAqui(null)
    setError(null)
    setAviso(null)
  }

  async function usarAqui() {
    setUbicando(true)
    setError(null)
    try {
      const p = await posicionPrecisa()
      if (!p) {
        setError('No se pudo leer la ubicación. Escribe la dirección.')
        return
      }
      setAqui({ latitud: p.coords.latitude, longitud: p.coords.longitude })
      // El GPS no necesita señal, pero convertirlo en dirección sí: se deja
      // el punto y una referencia que el técnico puede corregir.
      if (!dondeLaDeja.trim()) setDondeLaDeja('Donde marqué en el mapa')
    } finally {
      setUbicando(false)
    }
  }

  async function confirmarRecogida(h: MiHerramienta) {
    setError(null)
    setAviso(null)
    // Si la dirección es la de la obra y no tomó GPS, va el punto de la obra.
    const deLaObra = !aqui && dondeLaDeja.trim() === (h.direccion_obra ?? '').trim()
    try {
      const r = await pedirRecogida.mutateAsync({
        prestamoId: h.prestamo_id,
        direccion: dondeLaDeja,
        latitud: aqui?.latitud ?? (deLaObra ? h.latitud_obra : null),
        longitud: aqui?.longitud ?? (deLaObra ? h.longitud_obra : null),
        nota,
      })
      setRecogiendo(null)
      setAviso(
        r.enCola
          ? 'Sin señal: la petición quedó guardada en el teléfono y sale sola en cuanto haya señal.'
          : 'Recogida pedida. El gerente asigna quién pasa por ella; te avisamos.',
      )
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo pedir la recogida.')
    }
  }

  async function cancelar(h: MiHerramienta) {
    setError(null)
    setAviso(null)
    try {
      await cancelarRecogida.mutateAsync({ prestamoId: h.prestamo_id, recogidaId: h.recogida_id })
      setAviso('Recogida cancelada. La herramienta sigue a tu nombre.')
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo cancelar.')
    }
  }

  async function pedir(prestamoId: string) {
    setError(null)
    setAviso(null)
    try {
      const r = await solicitar.mutateAsync({
        prestamoId,
        ordenDestinoId: aQuien === 'orden' ? destino : null,
        colaboradorDestinoId: aQuien === 'tecnico' ? destino : null,
        motivo,
      })
      setTrasladando(null)
      setDestino('')
      setMotivo('')
      setAviso(
        r.enCola
          ? 'Sin señal: el traslado quedó guardado en el teléfono y sale solo en cuanto haya señal.'
          : 'Traslado solicitado. Queda pendiente de que gerencia lo apruebe.',
      )
    } catch (fallo) {
      setError(fallo instanceof Error ? fallo.message : 'No se pudo solicitar el traslado.')
    }
  }

  /*
   * LA HERRAMIENTA COMO UNA TARJETA MÁS DEL TÉCNICO. Johan: «¿pero para
   * devolver, trasladar, cómo le sale al técnico?». Salía un encabezado que se
   * montaba —«Se devuelve escaneándola en bodega» cortado a la derecha—, el
   * contenido pegado al borde y el formulario de traslado desbordado. Y
   * devolver no tenía botón: la única pista era ese rótulo cortado.
   *
   * Ahora cada herramienta es un bloque con sus dos acciones del mismo peso:
   * «Devolver» (que explica el paso real: en bodega, escaneando el QR) y
   * «Trasladar» (el formulario, dentro del bloque).
   */
  return (
    <section className={estilos.zona}>
      <header className={estilos.cabecera}>
        <span className={estilos.insignia} aria-hidden="true">
          <svg
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14.7 6.3a4 4 0 0 0-5.4 5.4L3.5 17.5a1.4 1.4 0 0 0 2 2l5.8-5.8a4 4 0 0 0 5.4-5.4l-2.5 2.5-2-2 2.5-2.5Z" />
          </svg>
        </span>
        <span className={estilos.titulo}>Herramienta a tu nombre</span>
        <span className={estilos.cuenta}>{herramientas.length}</span>
        {olvidada && <span className={estilos.alertaVieja}>más de 7 días</span>}
      </header>

      {/* Lo guardado sin señal todavía no está hecho: ámbar, no verde. */}
      {aviso && (
        <p className={aviso.startsWith('Sin señal') ? estilos.enCola : estilos.aviso}>{aviso}</p>
      )}
      {error && <p className={estilos.error}>{error}</p>}

      <ul className={estilos.lista}>
        {herramientas.map((h) => (
          <li key={h.prestamo_id} className={estilos.herramienta}>
            <div className={estilos.tope}>
              <span className={estilos.nombre}>{h.herramienta}</span>
              <span className={h.dias_afuera >= 7 ? estilos.diasViejos : estilos.dias}>
                {h.dias_afuera === 0
                  ? 'desde hoy'
                  : `${h.dias_afuera} ${h.dias_afuera === 1 ? 'día' : 'días'} contigo`}
              </span>
            </div>
            <p className={estilos.meta}>
              <b>{h.etiqueta}</b> · {h.orden_numero ? `para ${h.orden_numero}` : 'a tu nombre'}
            </p>

            <EstadoDeRecogida
              h={h}
              recogidaGuardada={Boolean(enColaDe('recogida', h.prestamo_id))}
              cancelacionGuardada={Boolean(enColaDe('cancelar-recogida', h.recogida_id))}
              trasladoGuardado={Boolean(enColaDe('traslado', h.prestamo_id))}
              cancelando={cancelarRecogida.isPending}
              onCancelar={() => void cancelar(h)}
            />

            {devolviendo === h.prestamo_id && (
              <div className={estilos.comoDevolver}>
                <b>Se devuelve en la bodega.</b> Llévala y escanea con la cámara del teléfono el
                código QR que tiene pegado: se abre la pantalla para entregarla. Desde la calle no
                se puede marcar como devuelta.
              </div>
            )}

            {recogiendo === h.prestamo_id ? (
              <div className={estilos.formTraslado}>
                <label className={estilos.rotuloCampo} htmlFor={`donde-${h.prestamo_id}`}>
                  ¿Dónde la dejas?
                </label>
                <input
                  id={`donde-${h.prestamo_id}`}
                  type="text"
                  className={estilos.campo}
                  placeholder="Dirección o sitio (portería, local 3…)"
                  value={dondeLaDeja}
                  onChange={(e) => setDondeLaDeja(e.target.value)}
                />
                {/* Un botón que se vea botón (Johan: «esto no parecen botones»):
                    borde, ícono de ubicación y, al tomarlo, en verde. */}
                <button
                  type="button"
                  className={`${estilos.botonGps} ${aqui ? estilos.botonGpsTomado : ''}`}
                  onClick={() => void usarAqui()}
                  disabled={ubicando}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="3.2" />
                    <path d="M12 2.5v3M12 18.5v3M2.5 12h3M18.5 12h3" />
                    <circle cx="12" cy="12" r="7" />
                  </svg>
                  {ubicando
                    ? 'Tomando la ubicación…'
                    : aqui
                    ? 'Punto tomado: aquí donde estoy'
                    : 'Usar aquí donde estoy (GPS)'}
                </button>
                <input
                  type="text"
                  className={estilos.campo}
                  placeholder="Nota para quien pasa (opcional): con quién la dejas"
                  value={nota}
                  onChange={(e) => setNota(e.target.value)}
                />
                <p className={estilos.nota}>Sigue a tu nombre hasta que la escaneen en bodega.</p>
                <div className={estilos.botones}>
                  <button
                    type="button"
                    className={estilos.secundario}
                    onClick={() => setRecogiendo(null)}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className={estilos.principal}
                    onClick={() => void confirmarRecogida(h)}
                    disabled={dondeLaDeja.trim().length === 0 || pedirRecogida.isPending}
                  >
                    {pedirRecogida.isPending ? 'Enviando…' : 'Pedir recogida'}
                  </button>
                </div>
              </div>
            ) : trasladando === h.prestamo_id ? (
              <div className={estilos.formTraslado}>
                <div className={estilos.aDonde} role="group" aria-label="A dónde va">
                  <button
                    type="button"
                    className={aQuien === 'orden' ? estilos.opcionViva : estilos.opcion}
                    aria-pressed={aQuien === 'orden'}
                    onClick={() => {
                      setAQuien('orden')
                      setDestino('')
                    }}
                  >
                    A otra orden
                  </button>
                  <button
                    type="button"
                    className={aQuien === 'tecnico' ? estilos.opcionViva : estilos.opcion}
                    aria-pressed={aQuien === 'tecnico'}
                    onClick={() => {
                      setAQuien('tecnico')
                      setDestino('')
                    }}
                  >
                    A un técnico
                  </button>
                </div>

                <select
                  className={estilos.campo}
                  value={destino}
                  onChange={(e) => setDestino(e.target.value)}
                  autoFocus
                >
                  {aQuien === 'orden' ? (
                    <>
                      <option value="">¿A qué orden se traslada?</option>
                      {ordenes
                        .filter((o) => o.numero !== h.orden_numero)
                        .map((o) => (
                          <option key={o.id} value={o.id}>
                            {o.numero} · {o.cliente ?? 'sin cliente'}
                          </option>
                        ))}
                    </>
                  ) : (
                    <>
                      <option value="">¿Quién se la queda?</option>
                      {colaboradores.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.nombre}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <input
                  type="text"
                  className={estilos.campo}
                  placeholder="Motivo (por qué se mueve)"
                  value={motivo}
                  onChange={(e) => setMotivo(e.target.value)}
                />
                <p className={estilos.nota}>Sigue a tu nombre hasta que el gerente lo apruebe.</p>
                <div className={estilos.botones}>
                  <button
                    type="button"
                    className={estilos.secundario}
                    onClick={() => {
                      setTrasladando(null)
                      setDestino('')
                      setMotivo('')
                    }}
                  >
                    Cancelar
                  </button>
                  <button
                    type="button"
                    className={estilos.principal}
                    onClick={() => void pedir(h.prestamo_id)}
                    disabled={!destino || motivo.trim().length === 0 || solicitar.isPending}
                  >
                    {solicitar.isPending ? 'Enviando…' : 'Pedir traslado'}
                  </button>
                </div>
              </div>
            ) : (
              <div className={estilos.acciones}>
                <button
                  type="button"
                  className={`${estilos.accion} ${
                    devolviendo === h.prestamo_id ? estilos.accionViva : ''
                  }`}
                  aria-expanded={devolviendo === h.prestamo_id}
                  onClick={() =>
                    setDevolviendo((d) => (d === h.prestamo_id ? null : h.prestamo_id))
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9 14 4 9l5-5" />
                    <path d="M4 9h10.5a5.5 5.5 0 0 1 0 11H11" />
                  </svg>
                  Devolver
                </button>
                <button
                  type="button"
                  className={estilos.accion}
                  onClick={() => {
                    setTrasladando(h.prestamo_id)
                    setRecogiendo(null)
                    setDevolviendo(null)
                    setDestino('')
                    setMotivo('')
                    setError(null)
                    setAviso(null)
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M4 8h14l-3-3M20 16H6l3 3" />
                  </svg>
                  Trasladar
                </button>
                <button
                  type="button"
                  className={estilos.accion}
                  onClick={() => abrirRecogida(h)}
                  // Una recogida viva por herramienta: la base tampoco deja dos.
                  disabled={
                    Boolean(h.recogida_estado) || Boolean(enColaDe('recogida', h.prestamo_id))
                  }
                >
                  <svg
                    viewBox="0 0 24 24"
                    width="17"
                    height="17"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M3 7h11v9H3zM14 10h4l3 3v3h-7z" />
                    <circle cx="7" cy="17.5" r="1.8" />
                    <circle cx="17" cy="17.5" r="1.8" />
                  </svg>
                  Recogida
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </section>
  )
}

/**
 * En qué va la recogida de esta herramienta, y lo que espera señal.
 * «Guardada» manda sobre lo que diga el servidor: es lo más reciente.
 */
function EstadoDeRecogida({
  h,
  recogidaGuardada,
  cancelacionGuardada,
  trasladoGuardado,
  cancelando,
  onCancelar,
}: {
  h: MiHerramienta
  recogidaGuardada: boolean
  cancelacionGuardada: boolean
  trasladoGuardado: boolean
  cancelando: boolean
  onCancelar: () => void
}) {
  const estado = cancelacionGuardada ? null : recogidaGuardada ? 'guardada' : h.recogida_estado

  return (
    <>
      {estado && (
        <div className={estilos.recogida}>
          <p>
            {estado === 'guardada' ? (
              <>
                <b>Recogida guardada en el teléfono.</b> Sale sola en cuanto haya señal.
              </>
            ) : estado === 'pedida' ? (
              <>
                <b>Pediste que la recojan</b> en {h.recogida_direccion}. Falta que el gerente asigne
                quién pasa.
              </>
            ) : (
              <>
                <b>{h.recogida_quien ?? 'Un operario'} va por ella</b> a {h.recogida_direccion}.
              </>
            )}{' '}
            Sigue a tu nombre hasta que la escaneen en bodega.
          </p>
          <button
            type="button"
            className={estilos.cancelarRecogida}
            onClick={onCancelar}
            disabled={cancelando}
          >
            Cancelar
          </button>
        </div>
      )}
      {cancelacionGuardada && (
        <p className={estilos.enCola}>Cancelación guardada en el teléfono: sale sola con señal.</p>
      )}
      {trasladoGuardado && (
        <p className={estilos.enCola}>Traslado guardado en el teléfono: sale solo con señal.</p>
      )}
    </>
  )
}
